import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { environment } from '../../config/environment'

interface Building {
  id: string
  buildingId: string
  level: number
  type: string
  bonus: number
}

interface Kingdom {
  id: string
  name: string
  level: number
  prestige: number
  buildings: Building[]
  backgroundSVG?: string
}

const buildingEmojis: Record<string, string> = {
  academy: '🏫',
  playground: '🎡',
  library: '📚',
  workshop: '🔨',
}

const buildingBonuses: Record<string, string> = {
  academy: '+Intelligence Training',
  playground: '+Happiness',
  library: '+Learning Speed',
  workshop: '+Item Crafting',
}

export function KingdomView(): React.ReactElement {
  const [kingdom, setKingdom] = useState<Kingdom | null>(null)
  const [loading, setLoading] = useState(false)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [kingdomName, setKingdomName] = useState('')

  useEffect(() => {
    fetchKingdom()
  }, [])

  const fetchKingdom = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('authToken')
      const response = await axios.get(`${environment.API_URL}/kingdom`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      setKingdom(response.data)
    } catch (error: any) {
      if (error.response?.status === 404) {
        setShowCreateForm(true)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleCreateKingdom = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!kingdomName.trim()) return

    try {
      const token = localStorage.getItem('authToken')
      const response = await axios.post(
        `${environment.API_URL}/kingdom`,
        { name: kingdomName },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      setKingdom(response.data)
      setKingdomName('')
      setShowCreateForm(false)
    } catch (error) {
      console.error('Failed to create kingdom', error)
    }
  }

  const handleUpgradeBuilding = async (buildingId: string) => {
    try {
      const token = localStorage.getItem('authToken')
      const response = await axios.post(
        `${environment.API_URL}/kingdom/upgrade-building`,
        { buildingId },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      setKingdom(response.data)
    } catch (error) {
      console.error('Failed to upgrade building', error)
    }
  }

  if (loading) {
    return <div className="text-center text-gray-500">Loading...</div>
  }

  if (!kingdom) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 max-w-md mx-auto">
        <h2 className="text-2xl font-bold text-center mb-4">Create Your Kingdom</h2>
        <form onSubmit={handleCreateKingdom} className="space-y-4">
          <input
            type="text"
            placeholder="Kingdom name"
            value={kingdomName}
            onChange={(e) => setKingdomName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            required
          />
          <button
            type="submit"
            className="w-full bg-purple-500 text-white py-2 rounded-md font-semibold hover:bg-purple-600"
          >
            Create Kingdom
          </button>
        </form>
      </div>
    )
  }

  return (
    <div className="bg-gradient-to-br from-yellow-50 to-green-50 rounded-lg shadow-lg p-6 max-w-2xl">
      {/* Background SVG */}
      {kingdom.backgroundSVG && (
        <div
          dangerouslySetInnerHTML={{ __html: kingdom.backgroundSVG }}
          className="w-full h-48 mb-4 rounded-lg overflow-hidden"
        />
      )}

      {/* Kingdom Header */}
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-gray-800">{kingdom.name}</h2>
        <div className="flex gap-4 mt-2">
          <div className="bg-blue-100 px-3 py-1 rounded-full">
            <span className="font-semibold">Level {kingdom.level}</span>
          </div>
          <div className="bg-purple-100 px-3 py-1 rounded-full">
            <span className="font-semibold">Prestige {kingdom.prestige}</span>
          </div>
        </div>
      </div>

      {/* Buildings Grid */}
      <div className="grid grid-cols-2 gap-3">
        {kingdom.buildings.map((building) => (
          <div
            key={building.id}
            className="bg-white border-2 border-yellow-300 rounded-lg p-3 hover:border-yellow-500 transition"
          >
            <div className="text-3xl mb-2">{buildingEmojis[building.type] || '🏗️'}</div>
            <p className="font-semibold text-gray-800 capitalize">{building.type}</p>
            <p className="text-sm text-gray-600 mb-2">Level {building.level}</p>
            <p className="text-xs text-gray-500 mb-2">{buildingBonuses[building.type]}</p>
            <button
              onClick={() => handleUpgradeBuilding(building.buildingId)}
              className="w-full bg-yellow-500 text-white py-1 rounded font-semibold hover:bg-yellow-600 text-sm"
            >
              Upgrade
            </button>
          </div>
        ))}
      </div>

      {/* Stats */}
      <div className="mt-6 pt-4 border-t-2 border-yellow-300">
        <p className="text-sm text-gray-600">
          <span className="font-semibold">Total Building Levels:</span> {kingdom.buildings.reduce((sum, b) => sum + b.level, 0)}
        </p>
      </div>
    </div>
  )
}
